/**
 * Cloudinary Upload Utility
 * Handles all file uploads to Cloudinary (replaces Supabase Storage)
 * 
 * Cloud name is public and safe to store in code.
 * Uses unsigned upload preset (configured in Cloudinary dashboard).
 */

const CLOUD_NAME = 'dxv0x2e0s';
const UPLOAD_PRESET = 'fakeid_unsigned'; // Must be created in Cloudinary dashboard
const UPLOAD_URL = `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`;
const PROJECT_PREFIX = 'f-id'; // Root folder for all project assets

export interface CloudinaryUploadResult {
  url: string;          // Full HTTPS URL
  publicId: string;     // Cloudinary public_id for management
  secureUrl: string;    // Same as url (HTTPS)
}

/**
 * Upload a File or Blob to Cloudinary
 */
export async function uploadToCloudinary(
  file: File | Blob,
  folder: string,
  fileName?: string
): Promise<CloudinaryUploadResult> {
  // Determine resource type: use 'raw' for non-image files (ZIP, DOCX, etc.)
  const mimeType = file instanceof File ? file.type : (file as Blob).type;
  const isRaw = mimeType && !mimeType.startsWith('image/');
  const resourceType = isRaw ? 'raw' : 'image';
  const uploadUrl = `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/${resourceType}/upload`;

  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', UPLOAD_PRESET);
  formData.append('folder', `${PROJECT_PREFIX}/${folder}`);
  
  if (fileName) {
    formData.append('public_id', fileName);
  }

  const response = await fetch(uploadUrl, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('Cloudinary upload error:', errorText);
    throw new Error(`Upload failed: ${response.status}`);
  }

  const data = await response.json();
  
  return {
    url: data.secure_url,
    publicId: data.public_id,
    secureUrl: data.secure_url,
  };
}

/**
 * Upload a base64 image to Cloudinary
 */
export async function uploadBase64ToCloudinary(
  base64Photo: string,
  folder: string
): Promise<CloudinaryUploadResult> {
  const formData = new FormData();
  
  // Cloudinary accepts data URIs directly
  const dataUri = base64Photo.startsWith('data:') 
    ? base64Photo 
    : `data:image/png;base64,${base64Photo}`;
  
  formData.append('file', dataUri);
  formData.append('upload_preset', UPLOAD_PRESET);
  formData.append('folder', `${PROJECT_PREFIX}/${folder}`);

  const response = await fetch(UPLOAD_URL, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('Cloudinary upload error:', errorText);
    throw new Error(`Upload failed: ${response.status}`);
  }

  const data = await response.json();
  
  return {
    url: data.secure_url,
    publicId: data.public_id,
    secureUrl: data.secure_url,
  };
}

/**
 * Delete an image from Cloudinary (requires edge function for server-side auth)
 * For now, we skip deletion – Cloudinary free tier has enough storage.
 * Deletion can be added later via edge function with API secret.
 */
export async function deleteFromCloudinary(_publicId: string): Promise<void> {
  // Cloudinary deletion requires API secret (server-side only)
  // For now, we don't delete – images are overwritten or cleaned manually
  console.warn('Cloudinary deletion requires server-side implementation. Skipping.');
}

/**
 * Get a Cloudinary URL with transformations
 * Useful for thumbnails, resizing, etc.
 */
export function getCloudinaryUrl(
  publicId: string, 
  transforms?: string
): string {
  const base = `https://res.cloudinary.com/${CLOUD_NAME}/image/upload`;
  if (transforms) {
    return `${base}/${transforms}/${publicId}`;
  }
  return `${base}/${publicId}`;
}

/**
 * Add responsive width/quality transforms to a Cloudinary URL.
 * Inserts `w_<width>,q_auto,f_auto` after `/upload/`.
 * If the URL already contains transforms, they're preserved.
 */
export function optimizeCloudinaryUrl(url: string, width: number = 640): string {
  if (!isCloudinaryUrl(url)) return url;
  // Already has resize transforms
  if (/\/w_\d+/.test(url)) return url;
  // Insert transforms after /upload/
  return url.replace(
    /\/image\/upload\/(v\d+\/)/,
    `/image/upload/w_${width},q_auto,f_auto/$1`
  );
}

/**
 * Check if a URL is a Cloudinary URL
 */
export function isCloudinaryUrl(url: string): boolean {
  return url.includes('res.cloudinary.com') || url.includes('cloudinary.com');
}

/**
 * Check if a URL is a legacy Supabase Storage URL
 */
export function isSupabaseStorageUrl(url: string): boolean {
  return url.includes('supabase.co/storage');
}
